import { board } from "./gameBoard_v2.js";
import { DIRECTIONS, COMPUTER_USERID } from "../constants_v2.js";
import {
  isHoveringHexagon,
  isAvailable,
  isRotateAvailable,
  isSame,
  gameCoordState,
  getNeighbors,
  calculateDirection,
  nearestSide,
  pixels2gameCoords,
} from "./utils-game_v2.js";
import { colors } from "../colors_v2.js";
import { dateTimeUtils } from "../dateTimeUtils_v2.js";
import { matchService } from "../matches/matchService_v2.js";
import { isAnimationRunning } from "./animations_v2.js";

let downEventInProgress = false;
let mouseUpInProgress = false;
const mouseState = {};

/**
 * @brief Handles the initial pointer-down interaction on the game board.
 * @description Validates player permissions, match state, and board availability
 * before capturing the pointer and initializing the drag/click state.
 * @async
 * @param {PointerEvent} event - The native pointer event.
 * @param {HTMLElement} element - The board element receiving the event.
 */
const handleMouseDown = async (event, element) => {
  downEventInProgress = true;

  // Permission & State Validation
  const match = await validateInteractionState();
  if (!match) return abortInteraction();

  // Technical Guards (Animations & Captures)
  if (isAnimationRunning()) return abortInteraction();

  try {
    element.setPointerCapture(event.pointerId);

    // Coordinate Calculation
    const { mouseX, mouseY } = calculateMouse(event);
    const coords = pixels2gameCoords({ cx: mouseX, cy: mouseY });

    if (!isAvailable(coords).available) {
      return abortInteraction();
    }

    // Initialize Interaction State
    initializePointerState(coords);

    // Visual Feedback
    renderInteractionFeedback(match, coords, mouseX, mouseY);
  } catch (error) {
    console.error("MouseDown Error:", error);
    abortInteraction();
  }
};

/**
 * @brief Handles pointer movement across the game board.
 * @description Manages hex highlighting during hover and direction calculation
 * during an active drag-to-move interaction.
 * @async
 * @param {PointerEvent} event - The native pointer event.
 */
const handleMouseMove = async (event) => {
  // Permissions & State Validation
  // Reusing the helper to ensure only the active player can trigger updates
  const match = await validateInteractionState();
  if (!match) return;

  // Coordinate Calculation
  const { mouseX, mouseY } = calculateMouse(event);
  const hoverCoords = pixels2gameCoords({ cx: mouseX, cy: mouseY });

  // Logic Branching: Are we dragging or just looking?
  const isDragging = Object.keys(mouseState).includes("mouseDownCoords");

  if (isDragging) {
    handleDragInteraction(hoverCoords, mouseX, mouseY);
  } else {
    await handleHoverInteraction(hoverCoords);
  }
};

/**
 * @brief Handles the pointer-up event to finalize a move or action.
 * @description Validates the interaction, calculates the intended direction
 * based on drag distance, and executes the player's action.
 * @async
 * @param {PointerEvent} event - The native pointer event.
 * @param {HTMLElement} element - The board element.
 */
const handleMouseUp = async (event, element) => {
  element.releasePointerCapture(event.pointerId);

  // Concurrency Guard
  if (mouseUpInProgress) return;
  mouseUpInProgress = true;

  // Permission & State Validation
  const match = await validateInteractionState();
  if (!match || !mouseState.mouseDownCoords) {
    return resetMouseUpLock(200);
  }

  // Coordinate Processing
  const { mouseX, mouseY } = calculateMouse(event);
  const mouseUpCoords = pixels2gameCoords({ cx: mouseX, cy: mouseY });
  const mouseDownCoords = mouseState.mouseDownCoords;

  // Cache and clear down-state immediately to prevent double-processing
  const startTime = mouseState.mouseDownTime;
  clearInteractionState();

  // Direction & Action Logic
  await processPointerAction({
    match,
    mouseDownCoords,
    mouseUpCoords,
    mouseX,
    mouseY,
  });

  // Final Unlock
  resetMouseUpLock(50);
};

/**
 * @brief Handles the pointer leaving the canvas area.
 * @description Resets the hover state and ensures any active interaction
 * is safely aborted to prevent the UI from getting stuck.
 * @async
 */
const handleMouseLeave = async () => {
  // Navigation Guard
  // If we are looking at the past (rewinding), we don't want mouse
  // movements to trigger state changes.
  if (board.getRewindOffset() !== 0) return;

  // State Validation
  const currentMatchResult = matchService.getCurrentMatch();
  if (currentMatchResult.status !== 200) return;

  // Interaction Cleanup
  // If the user was dragging and left the canvas, we kill the drag.
  abortInteraction();

  // Visual Reset
  // Move the hover highlight 'off-screen' and refresh the grid.
  board.setHoveringHexagon({ s: -1, q: -1, r: -1 });
  await board.renderGrid();
};

/**
 * @brief Determines the movement direction and executes the game action.
 * @description Logic branches between a single-hex tap (nearest side)
 * and a multi-hex drag (relative direction).
 * @async
 */
const processPointerAction = async ({
  match,
  mouseDownCoords,
  mouseUpCoords,
  mouseX,
  mouseY,
}) => {
  const player = matchService.getTurn().player;
  let direction;

  const isSameHex = isSame({
    coords1: mouseUpCoords,
    coords2: mouseDownCoords,
  });

  if (isSameHex) {
    // Tap/Click inside the same hexagon
    direction = nearestSide({ coords: mouseUpCoords, mouseX, mouseY });
  } else {
    // Dragged to a neighbor or beyond
    direction = calculateDirection({
      fromCoords: mouseDownCoords,
      toCoords: mouseUpCoords,
    });
  }

  // If the drag was valid (or it was a same-hex tap)
  if (direction !== "NotANeighbor") {
    matchService.setDirection({ player, direction });
    await board.takeAction({ coords: mouseDownCoords, player });
  }
};

/**
 * @brief Updates the visual highlight as the player hovers over hexagons.
 * @description Compares the current hover coordinates with the cached state
 * to determine if the grid needs a re-render.
 * @async
 * @param {Object} coords - The game coordinates currently under the pointer.
 */
const handleHoverInteraction = async (coords) => {
  const hasMovedToNewHex = !isHoveringHexagon(coords);

  if (hasMovedToNewHex) {
    board.setHoveringHexagon(coords);
  }

  // We render the grid to ensure the highlight follows the mouse smoothly
  await board.renderGrid();
};

/**
 * @brief Updates the direction indicator during a drag-to-move action.
 * @description Only triggers if the starting hexagon is a valid move target
 * and the current action isn't a 'remove' action.
 * @param {Object} hoverCoords - The coordinates the pointer is currently over.
 * @param {number} mouseX - Current horizontal pixel position.
 * @param {number} mouseY - Current vertical pixel position.
 */
const handleDragInteraction = (hoverCoords, mouseX, mouseY) => {
  const turn = matchService.getTurn().player;
  const currentAction = matchService.getAction(turn);
  const startCoords = mouseState.mouseDownCoords;

  const isOriginAvailable = isAvailable(startCoords).available;
  const isValidActionType = currentAction !== "remove";

  if (isOriginAvailable && isValidActionType) {
    mouseDirectionDrag({
      coords: hoverCoords,
      mouseX,
      mouseY,
    });
  }
};

/**
 * @brief Manages the visual feedback and logic while dragging to set a direction.
 * @description Calculates the intended direction, validates it against the current
 * player action (push/rotate), and renders directional arrows on the grid.
 * @async
 * @param {Object} params - The drag parameters.
 * @param {Object} params.coords - The game coordinates currently being hovered.
 * @param {number} params.mouseX - Current mouse X position.
 * @param {number} params.mouseY - Current mouse Y position.
 */
const mouseDirectionDrag = async ({ coords, mouseX, mouseY }) => {
  // Calculate the intended direction
  const direction = getIntentDirection(coords, mouseX, mouseY);

  if (direction === "NotANeighbor") {
    await board.renderGrid();
    return;
  }

  // Validate the direction
  const player = matchService.getTurn().player;
  const action = matchService.getAction(player);
  const { validDirections, isBlocked } = getActionValidation(
    player,
    action,
    direction,
  );

  // Render the UI
  renderDragVisuals({
    player,
    direction,
    validDirections,
    isBlocked,
  });
};

/**
 * @brief Checks if the current user and match state allow for an interaction.
 * @description Validates player identity, turn status, and match phase.
 * @async
 * @returns {Object|null} The match object if valid, otherwise null.
 */
const validateInteractionState = async () => {
  // Check Identity
  if (!(await matchService.pAIsPlayer())) return null;

  // Check Match Existence
  const result = matchService.getCurrentMatch();
  if (result.status !== 200) return null;

  const match = result.currentMatch;

  // Check Turn Logic
  const isComputerTurn = match.turn === COMPUTER_USERID;
  const isOpponentTurn =
    match.mode === "ownDevice" && !(await matchService.isPATurn());

  if (isComputerTurn || isOpponentTurn) return null;

  // Check Game Phase
  const isValidPhase = ["pending", "started"].includes(match.state);
  const isRewinding = board.getRewindOffset() !== 0;

  if (!isValidPhase || isRewinding) return null;

  return match;
};

/**
 * @brief Determines the direction based on mouse position.
 * @description If the mouse is still inside the starting hex, it finds the nearest side.
 * Otherwise, it calculates the direction from the start hex to the current hex.
 * @returns {string} The calculated direction string or "NotANeighbor".
 */
const getIntentDirection = (coords, mouseX, mouseY) => {
  if (isHoveringHexagon(coords)) {
    return nearestSide({ coords, mouseX, mouseY });
  }

  return calculateDirection({
    fromCoords: mouseState.mouseDownCoords,
    toCoords: coords,
  });
};

/**
 * @brief Validates if the chosen direction is legal for the current action.
 * @description Handles specific logic for 'rotate' availability and 'push' actions.
 * @param {string} player - Current player.
 * @param {string} action - Current action (e.g., 'push', 'rotate').
 * @param {string} direction - The chosen direction.
 * @returns {Object} Valid directions list and a blocked status flag.
 */
const getActionValidation = (player, action, direction) => {
  let validDirections = DIRECTIONS; // Constant representing all 6 hex directions
  let isBlocked = false;

  if (action === "rotate") {
    const rotationStatus = isRotateAvailable({
      coords: mouseState.mouseDownCoords,
      turnAsPlayer: player,
    });

    validDirections = rotationStatus.validDirections;

    const currentFacing = gameCoordState(mouseState.mouseDownCoords).direction;
    const isIllegalRotation = !validDirections.includes(direction);
    const isAlreadyFacing = direction === currentFacing;

    if (isIllegalRotation || isAlreadyFacing) {
      isBlocked = true;
    }
  }

  return { validDirections, isBlocked };
};

/**
 * Tells you the canvas coordinates of the mouse
 * @param {event} event - The javascript event
 * @returns {object} { mouseX, mouseY }
 */
const calculateMouse = (event) => {
  const rect = canvas.getBoundingClientRect();
  const mouseX = Math.floor(event.clientX - rect.left);
  const mouseY = Math.floor(event.clientY - rect.top);
  return { mouseX, mouseY };
};

/**
 * @brief Clears temporary interaction data and flags.
 * @description Resets the downEventInProgress flag and purges mouseDown metadata from gameState.
 */
const abortInteraction = () => {
  delete mouseState.mouseDownCoords;
  delete mouseState.mouseDownTime;

  clearDownEventInProgress();

  return false; // Useful for returning from guard clauses
};

/**
 * @brief Renders immediate visual feedback on the grid during a mouse down.
 * @description Draws the grid, available moves, and direction indicators.
 */
const renderInteractionFeedback = (match, coords, mouseX, mouseY) => {
  const availables = board.drawTheGrid(match);

  board.drawTheAvailables({
    availables,
    borderColor: colors.getGameColor("hexGrid"),
  });

  const playerTurn = matchService.getTurn().player;
  const currentAction = matchService.getAction(playerTurn);

  // If we aren't removing, we are likely placing/moving, so show direction
  if (currentAction !== "remove") {
    board.drawDirectionOnGrid({
      direction: matchService.getDirection(playerTurn),
      color: colors.getPlayerColor({ player: playerTurn, property: "hover" }),
      coords: coords,
    });

    mouseDirectionDrag({ coords, mouseX, mouseY });
  }
};

/**
 * @brief Renders the grid, the selected direction, and valid movement arrows.
 * @param {Object} config - Configuration for rendering (player, direction, etc).
 */
const renderDragVisuals = ({
  player,
  direction,
  validDirections,
  isBlocked,
}) => {
  // Clear and redraw base grid state
  const availables = board.drawTheGrid();
  board.drawTheAvailables({
    availables,
    borderColor: colors.getGameColor("hexGrid"),
  });

  // Highlight the current chosen direction
  board.drawDirectionOnGrid({
    direction,
    color: colors.getPlayerColor({ player, property: "hover" }),
    coords: mouseState.mouseDownCoords,
  });

  // Render valid movement arrows
  const neighbors = getNeighbors(mouseState.mouseDownCoords);
  const directionMap = [
    "up",
    "upRight",
    "downRight",
    "down",
    "downLeft",
    "upLeft",
  ];

  neighbors.forEach((neighbor, index) => {
    const dirName = directionMap[index];
    const isValidDirection = validDirections.includes(dirName);
    const isOnGrid = neighbor.coords.s !== -99;

    if (isValidDirection && isOnGrid) {
      board.renderArrow({
        centeredOnCoords: mouseState.mouseDownCoords,
        neighborCoords: neighbor.coords,
      });
    }
  });
};

/**
 * @brief Sets the initial game state for a pointer interaction.
 * @param {Object} coords - The game coordinates where the pointer went down.
 */
const initializePointerState = (coords) => {
  mouseState.mouseDownCoords = coords;
  board.setHoveringHexagon(coords); // Critical for mobile 'hover' simulation
  mouseState.mouseDownTime = dateTimeUtils.epochTimeMs();
};

/**
 * @brief Purges temporary coordinate data from the mouse state.
 */
const clearInteractionState = () => {
  delete mouseState.mouseDownCoords;
  delete mouseState.mouseDownTime;
};

/**
 * @brief Resets the mouse lock after a specified delay.
 * @description Prevents "ghost clicks" or rapid-fire events from triggering multiple actions.
 * @param {number} delay - Milliseconds to wait before allowing the next interaction.
 */
const resetMouseUpLock = (delay) => {
  setTimeout(() => {
    mouseUpInProgress = false;
    clearDownEventInProgress();
  }, delay);
};

const clearDownEventInProgress = () => {
  downEventInProgress = false;
};

const handlePointerOver = (event, element) => {
  element.classList.add("shadow");
  element.style.background = colors.getGameColor("iPop");
};

const handlePointerOut = (event, element) => {
  element.classList.remove("shadow");
  element.style.background = colors.getGameColor("lighterAccent");
};

export const mouse = {
  handleMouseDown,
  handleMouseLeave,
  handleMouseMove,
  handleMouseUp,
  clearDownEventInProgress,
  handlePointerOver,
  handlePointerOut,
};
